import { http } from "@shared/utils/request";
import type { FetchListOptions, ListResult } from "@shared/types/list";
import { mapCorpListItemToRow, type CorpAddressApiItem, type CorpContactApiItem, type CorpDetailApiItem, type CorpListApiItem, type CorpRow } from "@app/views/corp/list/config/data";
import { toCorpListRequestParams, type CorpQueryParam } from "@app/views/corp/list/config/filter";

export interface CorpOptionItem {
    id: string;
    corpCode: string;
    corpName: string;
    cooperationStatus: number;
}

interface CorpOptionsData {
    items: CorpOptionItem[];
}

/** 企业下拉选项；cooperationStatus=1 仅合作中；unboundChannelOnly 仅未绑定渠道（传 1） */
export function fetchCorpOptions(params?: { cooperationStatus?: number; unboundChannelOnly?: boolean | 0 | 1 }): Promise<CorpOptionsData> {
    const query: { cooperationStatus?: number; unboundChannelOnly?: 0 | 1 } = {};
    if (params?.cooperationStatus != null) query.cooperationStatus = params.cooperationStatus;
    if (params?.unboundChannelOnly) query.unboundChannelOnly = 1;
    return http.get<CorpOptionsData>("/manage/corp/options", query);
}

interface CorpListApiData {
    items: CorpListApiItem[];
    total: number;
}

export function fetchCorpList(params: CorpQueryParam, options?: FetchListOptions): Promise<ListResult<CorpRow>> {
    return http.post<CorpListApiData>("/manage/corp/list", toCorpListRequestParams(params), { signal: options?.signal }).then(res => ({
        list: (res.items || []).map(mapCorpListItemToRow),
        total: res.total ?? 0,
    }));
}

export type { CorpDetailApiItem, CorpListApiItem, CorpAddressApiItem, CorpContactApiItem };

export function fetchCorpDetail(id: string): Promise<CorpDetailApiItem> {
    return http.post<CorpDetailApiItem>("/manage/corp/detail", { id });
}

export interface CorpEditPayload {
    id: string;
    corpName: string;
    corpShortName?: string;
    orgType?: number;
    cooperationStatus: number;
    remark?: string;
}

export function editCorp(data: CorpEditPayload): Promise<void> {
    return http.post<void>("/manage/corp/edit", data);
}

export interface CorpAddressSavePayload {
    corpId: string;
    addressName: string;
    country: string;
    province?: string;
    city?: string;
    district?: string;
    detailAddress?: string;
}

export interface CorpAddressEditPayload extends CorpAddressSavePayload {
    id: string;
}

export interface CorpAddressDeletePayload {
    id: string;
    corpId: string;
}

export function addCorpAddress(data: CorpAddressSavePayload): Promise<void> {
    return http.post<void>("/manage/corp/address/add", data);
}

export function editCorpAddress(data: CorpAddressEditPayload): Promise<void> {
    return http.post<void>("/manage/corp/address/edit", data);
}

export function deleteCorpAddress(data: CorpAddressDeletePayload): Promise<void> {
    return http.post<void>("/manage/corp/address/delete", data);
}

export interface CorpContactSavePayload {
    corpId: string;
    contactName: string;
    role: number;
    telephone?: string;
    email?: string;
}

export interface CorpContactEditPayload extends CorpContactSavePayload {
    id: string;
}

export interface CorpContactDeletePayload {
    id: string;
    corpId: string;
}

export function addCorpContact(data: CorpContactSavePayload): Promise<void> {
    return http.post<void>("/manage/corp/contact/add", data);
}

export function editCorpContact(data: CorpContactEditPayload): Promise<void> {
    return http.post<void>("/manage/corp/contact/edit", data);
}

export function deleteCorpContact(data: CorpContactDeletePayload): Promise<void> {
    return http.post<void>("/manage/corp/contact/delete", data);
}
